

import { AnimationEvent } from '@angular/animations';
import { ComponentType, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ChangeDetectorRef, Directive, EventEmitter, Injector, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { MessageConfig, MwcConfigService } from 'mwc/core/config';
import { MwcSingletonService } from 'mwc/core/services';

import { MwcMessageData, MwcMessageDataOptions } from './typings';

let globalCounter = 0;

export abstract class MwcMNService {
  protected abstract componentPrefix: string;
  protected container?: MwcMNContainerComponent;

  constructor(
    protected mwcSingletonService: MwcSingletonService,
    protected overlay: Overlay,
    private injector: Injector
  ) {}

  remove(id?: string): void {
    if (this.container) {
      if (id) {
        this.container.remove(id);
      } else {
        this.container.removeAll();
      }
    }
  }

  protected getInstanceId(): string {
    return `${this.componentPrefix}-${globalCounter++}`;
  }

  protected withContainer<T extends MwcMNContainerComponent>(ctor: ComponentType<T>): T {
    let containerInstance = this.mwcSingletonService.getSingletonWithKey(this.componentPrefix);
    if (containerInstance) {
      return containerInstance as T;
    }

    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      positionStrategy: this.overlay.position().global()
    });
    const componentPortal = new ComponentPortal(ctor, null, this.injector);
    const componentRef = overlayRef.attach(componentPortal);
    const overlayPane = overlayRef.overlayElement;
    overlayPane.style.zIndex = '1010';

    if (!containerInstance) {
      this.container = containerInstance = componentRef.instance;
      this.mwcSingletonService.registerSingletonWithKey(this.componentPrefix, containerInstance);
    }

    return containerInstance as T;
  }
}

@Directive()
export abstract class MwcMNContainerComponent implements OnInit, OnDestroy {
  config?: Required<MessageConfig>;
  instances: Array<Required<MwcMessageData>> = [];

  protected readonly destroy$ = new Subject<void>();

  constructor(protected cdr: ChangeDetectorRef, protected mwcConfigService: MwcConfigService) {
    this.updateConfig();
  }

  ngOnInit(): void {
    this.subscribeConfigChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  create(data: MwcMessageData): Required<MwcMessageData> {
    const instance = this.onCreate(data);

    if (this.instances.length >= this.config!.mwcMaxStack) {
      this.instances = this.instances.slice(1);
    }

    this.instances = [...this.instances, instance];

    this.readyInstances();

    return instance;
  }

  remove(id: string, userAction: boolean = false): void {
    this.instances.some((instance, index) => {
      if (instance.messageId === id) {
        this.instances.splice(index, 1);
        this.instances = [...this.instances];
        this.onRemove(instance, userAction);
        this.readyInstances();
        return true;
      }
      return false;
    });
  }

  removeAll(): void {
    this.instances.forEach(i => this.onRemove(i, false));
    this.instances = [];

    this.readyInstances();
  }

  protected onCreate(instance: MwcMessageData): Required<MwcMessageData> {
    instance.options = this.mergeOptions(instance.options);
    instance.onClose = new Subject<boolean>();
    return instance as Required<MwcMessageData>;
  }

  protected onRemove(instance: Required<MwcMessageData>, userAction: boolean): void {
    instance.onClose.next(userAction);
    instance.onClose.complete();
  }

  protected readyInstances(): void {
    this.cdr.detectChanges();
  }

  protected abstract updateConfig(): void;

  protected abstract subscribeConfigChange(): void;

  protected mergeOptions(options?: MwcMessageDataOptions): MwcMessageDataOptions {
    const { mwcDuration, mwcAnimate, mwcPauseOnHover } = this.config!;
    return { mwcDuration, mwcAnimate, mwcPauseOnHover, ...options };
  }
}

@Directive()
export abstract class MwcMNComponent implements OnInit, OnDestroy {
  instance!: Required<MwcMessageData>;
  index?: number;

  readonly destroyed = new EventEmitter<{ id: string; userAction: boolean }>();
  readonly animationStateChanged: Subject<AnimationEvent> = new Subject<AnimationEvent>();

  protected options!: Required<MwcMessageDataOptions>;
  protected autoClose?: boolean;
  protected closeTimer?: number;
  protected userAction: boolean = false;
  protected eraseTimer: number | null = null;
  protected eraseTimingStart?: number;
  protected eraseTTL!: number;

  protected constructor(protected cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.options = this.instance.options as Required<MwcMessageDataOptions>;

    if (this.options.mwcAnimate) {
      this.instance.state = 'enter';
      this.animationStateChanged
        .pipe(
          filter(event => event.phaseName === 'done' && event.toState === 'leave'),
          take(1)
        )
        .subscribe(() => {
          clearTimeout(this.closeTimer);
          this.destroyed.next({ id: this.instance.messageId, userAction: this.userAction });
        });
    }

    this.autoClose = this.options.mwcDuration > 0;

    if (this.autoClose) {
      this.initErase();
      this.startEraseTimeout();
    }
  }

  ngOnDestroy(): void {
    if (this.autoClose) {
      this.clearEraseTimeout();
    }
    this.animationStateChanged.complete();
  }

  onEnter(): void {
    if (this.autoClose && this.options.mwcPauseOnHover) {
      this.clearEraseTimeout();
      this.updateTTL();
    }
  }

  onLeave(): void {
    if (this.autoClose && this.options.mwcPauseOnHover) {
      this.startEraseTimeout();
    }
  }

  protected destroy(userAction: boolean = false): void {
    this.userAction = userAction;
    if (this.options.mwcAnimate) {
      this.instance.state = 'leave';
      this.cdr.detectChanges();
      this.closeTimer = setTimeout(() => {
        this.closeTimer = undefined;
        this.destroyed.next({ id: this.instance.messageId, userAction });
      }, 200);
    } else {
      this.destroyed.next({ id: this.instance.messageId, userAction });
    }
  }

  private initErase(): void {
    this.eraseTTL = this.options.mwcDuration;
    this.eraseTimingStart = Date.now();
  }

  private updateTTL(): void {
    if (this.autoClose) {
      this.eraseTTL -= Date.now() - this.eraseTimingStart!;
    }
  }

  private startEraseTimeout(): void {
    if (this.eraseTTL > 0) {
      this.clearEraseTimeout();
      this.eraseTimer = setTimeout(() => this.destroy(), this.eraseTTL);
      this.eraseTimingStart = Date.now();
    } else {
      this.destroy();
    }
  }

  private clearEraseTimeout(): void {
    if (this.eraseTimer !== null) {
      clearTimeout(this.eraseTimer);
      this.eraseTimer = null;
    }
  }
}
